import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import HoldingsDisplay from '../components/HoldingsDisplay';
import { useBalance } from '../contexts/BalanceContext';
import { useTheme } from '../contexts/ThemeContext';
import { LogOut, Wallet, Settings, Shield, HelpCircle, Users, Gift, MessageCircare, CreditCard, ArrowDownToLine, ArrowRightLeft, Link2, Crown, Gamepad2, MessageCircle, Eye, EyeOff, Star, Zap, TrendingUp, Check, X, FileText, AlertCircle, CheckCircle, Clock, Sun, Moon } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext';
// Temporarily disabled due to Firestore error
// import { useRealtimeWalletSync, useRealtimeUserSync } from '../hooks/useRealtimeSync';
import axios from 'axios';

const API_URL = '';

const Profile = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { balanceVisible, toggleBalanceVisibility, formatBalance } = useBalanceVisibility();
  const { refreshBalance, isAuthenticated } = useBalance();
  const { theme, toggleTheme, isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [vipStatus, setVipStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [kycStatus, setKycStatus] = useState(null);
  const [loadingKyc, setLoadingKyc] = useState(true);

  // Get user ID
  const userData = localStorage.getItem('user');
  const userId = userData ? JSON.parse(userData).id : null;

  // Real-time sync hooks - DISABLED due to Firestore error
  // const { wallet: realtimeWallet } = useRealtimeWalletSync(userId);
  // const { userData: realtimeUser } = useRealtimeUserSync(userId);
  const realtimeWallet = null; // Disabled
  const realtimeUser = null; // Disabled

  // Refresh balance when component mounts
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      refreshBalance();
    }
  }, [refreshBalance]);

  useEffect(() => {
    // Load user from localStorage
    if (userData) {
      setUser(JSON.parse(userData));
    }

    // All balance data now comes from BalanceContext (via HoldingsDisplay component)
    // No need for separate balance fetching - ensures data consistency

    // Fetch VIP status
    const fetchVIPStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API_URL}/api/vip/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          setVipStatus(response.data);
        }
      } catch (error) {
        console.error('Error fetching VIP status:', error);
      }
    };

    // Fetch KYC status
    const fetchKYCStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoadingKyc(false);
          return;
        }

        const response = await fetch(`${API_URL}/api/kyc/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setKycStatus(data);
        }
      } catch (error) {
        console.error('Error fetching KYC status:', error);
      } finally {
        setLoadingKyc(false);
      }
    };

    fetchVIPStatus();
    fetchKYCStatus();
    
    // Balance data is now managed by BalanceContext (auto-refreshes every 60s)
    // No need for separate intervals - ensures consistency
  }, []);

  // Update balance when Firebase data changes (real-time)
  useEffect(() => {
    if (realtimeWallet && realtimeWallet.balances) {
      // Refresh balance from BalanceContext to ensure consistency
      refreshBalance();
      
      // Show toast notification
      toast({
        title: "Balance Updated",
        description: "Your wallet balance has been updated by admin",
        duration: 3000
      });
    }
  }, [realtimeWallet]);

  // Update user info when Firebase data changes (real-time)
  useEffect(() => {
    if (realtimeUser) {
      const updatedUser = {
        ...user,
        ...realtimeUser
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }, [realtimeUser]);

  // All balance and portfolio calculations now handled by BalanceContext
  // Data flows through HoldingsDisplay component for consistency

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleSubscribeVIP = async (tier, cycle) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/vip/subscribe`, {
        tier: tier,
        billing_cycle: cycle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast({
          title: "Success!",
          description: response.data.message,
        });
        setShowVipModal(false);
        // Refresh VIP status
        const statusResponse = await axios.get(`${API_URL}/api/vip/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVipStatus(statusResponse.data);
        
        // Refresh balance from BalanceContext to ensure consistency
        await refreshBalance();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to subscribe to VIP",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClaimDailyBonus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/vip/claim-benefit`, {
        benefit_type: 'daily_bonus'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast({
          title: "Bonus Claimed!",
          description: `You received $${response.data.reward_amount} USDT`,
        });
        
        // Refresh balance from BalanceContext to ensure consistency
        await refreshBalance();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to claim bonus",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVIP = async () => {
    if (!window.confirm('Are you sure you want to cancel auto-renewal? Your VIP benefits will remain active until expiration.')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/vip/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast({
          title: "Cancelled",
          description: response.data.message,
        });
        
        // Refresh VIP status
        const statusResponse = await axios.get(`${API_URL}/api/vip/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVipStatus(statusResponse.data);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to cancel subscription",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const vipTiers = {
    silver: {
      name: 'Silver',
      monthly: 99,
      yearly: 999,
      color: 'from-gray-400 to-gray-600',
      icon: Star,
      features: ['0.08% Trading Fee', 'Priority Support', 'Weekly Insights']
    },
    gold: {
      name: 'Gold',
      monthly: 299,
      yearly: 2999,
      color: 'from-yellow-400 to-amber-600',
      icon: Crown,
      features: ['0.05% Trading Fee', '24/7 VIP Support', 'Daily Analysis', 'Trading Signals']
    },
    platinum: {
      name: 'Platinum',
      monthly: 999,
      yearly: 9999,
      color: 'from-purple-400 to-purple-600',
      icon: Zap,
      features: ['0% Trading Fee', 'Account Manager', 'Real-time Insights', 'Private Signals']
    }
  };

  const features = [
    { icon: Shield, label: 'AI Arbitrage', color: 'from-cyan-400 to-cyan-600', path: '/ai-arbitrage' },
    { icon: CreditCard, label: 'Recharge', color: 'from-cyan-400 to-cyan-600', path: '/recharge' },
    { icon: ArrowDownToLine, label: 'Withdraw', color: 'from-cyan-400 to-cyan-600', path: '/withdraw' },
    { icon: ArrowRightLeft, label: 'Transfer', color: 'from-cyan-400 to-cyan-600', path: '/transfer' },
    { icon: Link2, label: 'Connect Wallet', color: 'from-cyan-400 to-cyan-600', path: '/connect-wallet' },
    { icon: Crown, label: 'VIP', color: 'from-cyan-400 to-cyan-600', path: '/vip' },
    { icon: Gamepad2, label: 'Sim Trade', color: 'from-cyan-400 to-cyan-600', path: '/home' },
    { icon: MessageCircle, label: 'Live Chat', color: 'from-cyan-400 to-cyan-600', path: '/live-chat' },
  ];

  const menuItems = [
    { icon: Wallet, label: 'My Wallet', path: '/wallet' },
    { icon: Settings, label: 'Settings', path: '/settings' },
    { icon: Shield, label: 'Security', path: '/security' },
    { icon: Users, label: 'Referral Program', path: '/referral' },
    { icon: Gift, label: 'Rewards', path: '/rewards' },
    { icon: HelpCircle, label: 'Help & Support', path: '/support' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-950 pb-20">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header - Modern Design */}
      <div className="relative bg-gradient-to-br from-cyan-900/40 via-blue-900/40 to-purple-900/40 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-950/50"></div>
        <div className="relative px-4 pt-6 pb-8">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">
                My Profile
              </h1>
              <p className="text-xs text-gray-400 mt-1">Manage your account & preferences</p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {/* Theme Toggle Button */}
              <button 
                onClick={toggleTheme}
                className="relative p-3 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 hover:border-cyan-400/50 backdrop-blur-sm transition-all active:scale-95 shadow-lg shadow-cyan-500/20 group overflow-hidden"
                aria-label="Toggle theme"
              >
                {/* Animated background on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-blue-400/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                {/* Icon with smooth transition */}
                <div className="relative">
                  {isDark ? (
                    <Sun className="w-6 h-6 text-amber-400 transition-transform group-hover:rotate-90 group-hover:scale-110" />
                  ) : (
                    <Moon className="w-6 h-6 text-blue-400 transition-transform group-hover:rotate-12 group-hover:scale-110" />
                  )}
                </div>
              </button>

              {/* Settings Button */}
              <button 
                onClick={() => navigate('/settings')}
                className="p-3 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 hover:border-cyan-400/50 backdrop-blur-sm transition-all active:scale-95 shadow-lg shadow-cyan-500/20"
                aria-label="Settings"
              >
                <Settings className="w-6 h-6 text-cyan-400" />
              </button>
            </div>
          </div>
          
          {/* User Card - Enhanced Design */}
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-cyan-500/30 rounded-3xl p-5 backdrop-blur-xl shadow-2xl">
            {/* Animated Border Gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 rounded-3xl blur-xl opacity-50"></div>
            
            <div className="relative">
              {/* Profile Header */}
              <div className="flex items-center gap-4 mb-5">
                {/* Avatar with Ring */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full blur-md opacity-70 animate-pulse"></div>
                  <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-cyan-500/50 border-4 border-slate-900">
                    <Users className="w-10 h-10 text-white" />
                  </div>
                  {/* Online Status Indicator */}
                  <div className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 border-4 border-slate-900 rounded-full animate-pulse"></div>
                </div>

                {/* User Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-black text-xl text-white">{user?.username || 'User'}</h2>
                    {user?.user_number && (
                      <span className="px-3 py-1 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/50 rounded-full text-xs font-bold text-cyan-400 shadow-lg">
                        #{user.user_number.toString().padStart(4, '0')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-400 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    {user?.email || 'user@edgetrade.pro'}
                  </div>
                  {/* Quick Stats */}
                  <div className="flex items-center gap-3 mt-2">
                    <div className="px-2 py-1 bg-slate-700/50 rounded-lg">
                      <span className="text-xs text-gray-400">Level</span>
                      <span className="ml-1 text-xs font-bold text-cyan-400">1</span>
                    </div>
                    <div className="px-2 py-1 bg-slate-700/50 rounded-lg">
                      <span className="text-xs text-gray-400">Rank</span>
                      <span className="ml-1 text-xs font-bold text-purple-400">Trader</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Balance Card - Modern Glassmorphism */}
              <div className="relative overflow-hidden border-t border-cyan-500/20 pt-5 mt-1">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-purple-500/5 rounded-2xl"></div>
                
                <div className="relative">
                  {/* Unified Portfolio Display using HoldingsDisplay Component */}
                  <HoldingsDisplay 
                    showTotal={true}
                    showIndividual={true}
                    showLastUpdate={true}
                    showRefreshButton={true}
                    title="Portfolio Value"
                    compact={false}
                    className=""
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VIP Membership Section */}
        <div className="mt-4">
          {vipStatus?.is_vip ? (
            // Active VIP Member
            <div className="bg-gradient-to-br from-yellow-900/40 to-orange-900/40 border-2 border-yellow-500/50 rounded-xl p-5 shadow-xl shadow-yellow-500/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-yellow-400 font-bold text-lg">{vipStatus.tier_name} VIP</div>
                    <div className="text-yellow-300 text-xs">Level {vipStatus.level} Member</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-yellow-400 text-xs font-bold">Expires</div>
                  <div className="text-yellow-300 text-xs">{new Date(vipStatus.expires_at).toLocaleDateString()}</div>
                </div>
              </div>

              {/* VIP Benefits Display */}
              <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                <div className="text-white font-bold text-sm mb-3">Active Benefits</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-white text-xs">{vipStatus.trading_fee}% Trading Fee</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-cyan-400" />
                    <span className="text-white text-xs">${(vipStatus.withdrawal_limit/1000).toFixed(0)}K Limit</span>
                  </div>
                  {vipStatus.features?.slice(0, 2).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-white text-xs">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleClaimDailyBonus}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg text-white font-semibold transition-all disabled:opacity-50"
                >
                  <Gift className="w-4 h-4" />
                  <span className="text-sm">Daily Bonus</span>
                </button>
                <button
                  onClick={handleCancelVIP}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/50 hover:bg-red-500/30 rounded-lg text-red-400 font-semibold transition-all disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                  <span className="text-sm">Cancel</span>
                </button>
              </div>
            </div>
          ) : (
            // Non-VIP Member - Upgrade Prompt
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-cyan-500/30 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-yellow-400 font-bold text-lg">Upgrade to VIP</div>
                  <div className="text-cyan-300 text-xs">Unlock exclusive benefits</div>
                </div>
              </div>

              {/* Benefits Grid */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { icon: TrendingUp, text: 'Lower Fees' },
                  { icon: Zap, text: 'Priority Support' },
                  { icon: Star, text: 'Daily Bonus' }
                ].map((benefit, idx) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={idx} className="bg-slate-900/50 rounded-xl p-3 text-center border border-cyan-500/20">
                      <div className="w-10 h-10 mx-auto mb-2 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div className="text-white text-xs font-bold leading-tight">{benefit.text}</div>
                    </div>
                  );
                })}
              </div>

              {/* Quick Upgrade Button */}
              <button
                onClick={() => setShowVipModal(true)}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 rounded-lg text-white font-bold transition-all shadow-lg shadow-yellow-500/30"
              >
                View VIP Plans
              </button>
            </div>
          )}
        </div>
      </div>

      {/* KYC Status Section - Only show if not approved */}
      {!loadingKyc && kycStatus && kycStatus.kyc_status !== 'approved' && (
        <div className="px-4 mt-6">
          <div className={`relative overflow-hidden rounded-3xl p-5 border-2 ${
            kycStatus.kyc_status === 'not_submitted'
              ? 'bg-gradient-to-br from-red-900/40 to-orange-900/40 border-red-500/50'
              : kycStatus.kyc_status === 'pending'
              ? 'bg-gradient-to-br from-yellow-900/40 to-orange-900/40 border-yellow-500/50'
              : kycStatus.kyc_status === 'rejected'
              ? 'bg-gradient-to-br from-red-900/40 to-pink-900/40 border-red-500/50'
              : 'bg-gradient-to-br from-slate-800/40 to-slate-900/40 border-cyan-500/30'
          } backdrop-blur-xl shadow-2xl`}>
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-purple-500/5 to-pink-500/5 rounded-3xl blur-xl opacity-50"></div>
            
            <div className="relative">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {kycStatus.kyc_status === 'not_submitted' ? (
                    <div className="p-3 bg-red-500/20 rounded-2xl border border-red-500/50">
                      <AlertCircle className="w-6 h-6 text-red-400" />
                    </div>
                  ) : kycStatus.kyc_status === 'pending' ? (
                    <div className="p-3 bg-yellow-500/20 rounded-2xl border border-yellow-500/50">
                      <Clock className="w-6 h-6 text-yellow-400 animate-pulse" />
                    </div>
                  ) : (
                    <div className="p-3 bg-red-500/20 rounded-2xl border border-red-500/50">
                      <X className="w-6 h-6 text-red-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-white">KYC Verification</h3>
                    <p className="text-xs text-gray-400">
                      {kycStatus.kyc_status === 'not_submitted'
                        ? 'Identity verification not submitted'
                        : kycStatus.kyc_status === 'pending'
                        ? 'Under review by our team'
                        : kycStatus.kyc_status === 'rejected'
                        ? 'Verification was rejected'
                        : 'Verification status'}
                    </p>
                  </div>
                </div>

                {/* Status Badge */}
                <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                  kycStatus.kyc_status === 'not_submitted'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                    : kycStatus.kyc_status === 'pending'
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                    : 'bg-red-500/20 text-red-400 border border-red-500/50'
                }`}>
                  {kycStatus.kyc_status === 'not_submitted'
                    ? 'Not Submitted'
                    : kycStatus.kyc_status === 'pending'
                    ? 'Pending Review'
                    : kycStatus.kyc_status === 'rejected'
                    ? 'Rejected'
                    : kycStatus.kyc_status.charAt(0).toUpperCase() + kycStatus.kyc_status.slice(1)}
                </div>
              </div>

              {/* Message */}
              <div className="mb-4">
                <p className="text-sm text-gray-300">
                  {kycStatus.kyc_status === 'not_submitted' ? (
                    <>
                      <strong className="text-red-400">⚠️ Action Required:</strong> Please complete your KYC verification to unlock full platform features including higher withdrawal limits and advanced trading options.
                    </>
                  ) : kycStatus.kyc_status === 'pending' ? (
                    <>
                      <strong className="text-yellow-400">⏳ In Progress:</strong> Your KYC documents are currently under review. This typically takes 24-48 hours. We'll notify you once the review is complete.
                    </>
                  ) : kycStatus.kyc_status === 'rejected' ? (
                    <>
                      <strong className="text-red-400">❌ Verification Failed:</strong> {kycStatus.kyc_message || 'Your KYC submission was rejected. Please review your documents and submit again with valid information.'}
                    </>
                  ) : (
                    'Please verify your identity to access full platform features.'
                  )}
                </p>
              </div>

              {/* Action Button - Only show if not pending */}
              {(kycStatus.kyc_status === 'not_submitted' || kycStatus.kyc_status === 'rejected') && (
                <button
                  onClick={() => navigate('/kyc-submission')}
                  className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-white transition-all active:scale-95 shadow-lg ${
                    kycStatus.kyc_status === 'not_submitted'
                      ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600'
                      : 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span>{kycStatus.kyc_status === 'rejected' ? 'Resubmit KYC' : 'Complete KYC Verification'}</span>
                </button>
              )}

              {/* Pending - Show info */}
              {kycStatus.kyc_status === 'pending' && kycStatus.kyc_data && kycStatus.kyc_data.submitted_at && (
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl border border-yellow-500/20">
                  <span className="text-xs text-gray-400">Submitted on:</span>
                  <span className="text-xs font-bold text-yellow-400">
                    {new Date(kycStatus.kyc_data.submitted_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* VIP Subscription Modal */}
      {showVipModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500/50 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm p-4 border-b border-cyan-500/30 flex items-center justify-between">
              <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
                <Crown className="w-6 h-6" />
                Choose Your VIP Plan
              </h2>
              <button
                onClick={() => setShowVipModal(false)}
                className="p-3 hover:bg-slate-800 active:bg-slate-700 rounded-xl transition-all active:scale-95"
                aria-label="Close modal"
              >
                <X className="w-6 h-6 text-cyan-400" />
              </button>
            </div>

            <div className="p-6">
              {/* Billing Cycle Toggle */}
              <div className="flex justify-center mb-6">
                <div className="bg-slate-800/50 rounded-lg p-1 flex gap-1">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                      billingCycle === 'monthly'
                        ? 'bg-cyan-500 text-white'
                        : 'text-cyan-400 hover:bg-slate-700'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingCycle('yearly')}
                    className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                      billingCycle === 'yearly'
                        ? 'bg-cyan-500 text-white'
                        : 'text-cyan-400 hover:bg-slate-700'
                    }`}
                  >
                    Yearly <span className="text-green-400 text-xs ml-1">(Save 17%)</span>
                  </button>
                </div>
              </div>

              {/* VIP Tiers */}
              <div className="grid md:grid-cols-3 gap-4">
                {Object.entries(vipTiers).map(([key, tier]) => {
                  const Icon = tier.icon;
                  const price = billingCycle === 'monthly' ? tier.monthly : tier.yearly;
                  
                  return (
                    <div
                      key={key}
                      className={`bg-gradient-to-br ${tier.color} bg-opacity-10 border-2 ${
                        selectedTier === key ? 'border-cyan-400 shadow-xl shadow-cyan-500/50' : 'border-cyan-500/30'
                      } rounded-xl p-6 cursor-pointer transition-all hover:scale-105`}
                      onClick={() => setSelectedTier(key)}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 bg-gradient-to-br ${tier.color} rounded-full`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        {selectedTier === key && (
                          <div className="p-1 bg-cyan-500 rounded-full">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
                      <div className="text-3xl font-bold text-cyan-400 mb-1">${price}</div>
                      <div className="text-cyan-300 text-sm mb-4">/{billingCycle}</div>
                      
                      <div className="space-y-2">
                        {tier.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-white">
                            <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Subscribe Button */}
              <button
                onClick={() => selectedTier && handleSubscribeVIP(selectedTier, billingCycle)}
                disabled={!selectedTier || loading}
                className="w-full mt-6 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 rounded-xl text-white font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-yellow-500/30"
              >
                {loading ? 'Processing...' : selectedTier ? `Subscribe to ${vipTiers[selectedTier]?.name} VIP` : 'Select a Plan'}
              </button>

              <p className="text-center text-cyan-400 text-sm mt-4">
                Payment will be deducted from your USDT balance
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions - Responsive Flexible Grid */}
      <div className="relative px-4 py-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-black text-xl text-white">Quick Actions</h2>
            <p className="text-xs text-gray-400 mt-1">Fast access to key features</p>
          </div>
          <Zap className="w-5 h-5 text-cyan-400 animate-pulse" />
        </div>

        {/* Responsive Actions Grid - 2 cols on mobile, 4 on tablet/desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <button
                key={index}
                onClick={() => navigate(feature.path)}
                className="group relative overflow-hidden flex flex-col items-center justify-center gap-2 sm:gap-2.5 p-3 sm:p-4 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-cyan-500/20 hover:border-cyan-400/50 backdrop-blur-sm transition-all duration-300 active:scale-95 hover:scale-[1.02] hover:shadow-lg hover:shadow-cyan-500/20 min-h-[100px] sm:min-h-[110px]"
                aria-label={feature.label}
                style={{
                  animation: `slideUp 0.3s ease-out ${index * 0.05}s both`
                }}
              >
                {/* Hover Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-purple-500/0 group-hover:from-cyan-500/10 group-hover:to-purple-500/10 transition-all duration-300 rounded-2xl sm:rounded-3xl"></div>
                
                {/* Icon Container - Flexible sizing */}
                <div className="relative flex-shrink-0">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/20 to-purple-400/20 rounded-xl sm:rounded-2xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Label - Responsive text sizing */}
                <span className="relative text-[10px] sm:text-xs text-center text-gray-300 font-bold leading-tight group-hover:text-cyan-300 transition-colors px-1 break-words max-w-full">
                  {feature.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Menu Items - Modern List */}
      <div className="relative px-4">
        {/* Section Divider */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
          <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Settings & More</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
        </div>

        {/* Menu List */}
        <div className="space-y-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={() => navigate(item.path)}
                className="group w-full flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-slate-800/40 to-slate-900/40 border border-cyan-500/10 hover:border-cyan-400/30 hover:bg-slate-800/60 backdrop-blur-sm transition-all active:scale-[0.98] hover:shadow-lg hover:shadow-cyan-500/10"
                aria-label={item.label}
                style={{
                  animation: `slideUp 0.3s ease-out ${index * 0.05}s both`
                }}
              >
                <div className="flex items-center gap-4">
                  {/* Icon with Animated Background */}
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative p-3 rounded-xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 group-hover:from-cyan-500/20 group-hover:to-purple-500/20 border border-cyan-500/20 group-hover:border-cyan-400/40 transition-all">
                      <Icon className="w-6 h-6 text-cyan-400 group-hover:scale-110 transition-transform" />
                    </div>
                  </div>

                  {/* Label */}
                  <div className="flex-1 text-left">
                    <span className="font-bold text-white group-hover:text-cyan-300 transition-colors">{item.label}</span>
                  </div>

                  {/* Arrow Indicator */}
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-cyan-500/5 group-hover:bg-cyan-500/10 border border-cyan-500/10 group-hover:border-cyan-400/30 transition-all">
                    <svg className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Logout Button - Modern Style */}
      <div className="px-4 py-6">
        <button
          onClick={handleLogout}
          className="group relative w-full overflow-hidden flex items-center justify-center gap-3 py-5 rounded-2xl border-2 border-red-500/50 hover:border-red-500 bg-gradient-to-r from-red-900/10 to-red-800/10 hover:from-red-900/20 hover:to-red-800/20 text-red-400 font-bold hover:text-red-300 backdrop-blur-sm transition-all active:scale-[0.98] shadow-lg hover:shadow-red-500/20"
          aria-label="Logout"
        >
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 via-red-500/10 to-red-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
          
          {/* Icon with Rotation Effect */}
          <div className="relative">
            <LogOut className="w-6 h-6 group-hover:rotate-12 transition-transform" />
          </div>

          {/* Text */}
          <span className="relative text-base tracking-wide">Sign Out</span>
        </button>

        {/* App Version */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">EdgeTrade Pro v1.0.0</p>
          <p className="text-xs text-gray-600 mt-1">© 2025 All Rights Reserved</p>
        </div>
      </div>

      <BottomNav active="personal" />
    </div>
  );
};

export default Profile;